import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Minus, Plus, ShoppingBag, MessageCircle, ChevronLeft, Heart, Mail } from "lucide-react";
import api, { inr, WHATSAPP, resolveImg } from "../lib/api";
import { useCart } from "../context/CartContext";
import { useWishlist } from "../context/WishlistContext";
import { useSeo } from "../lib/seo";
import Stars from "../components/Stars";
import Reviews from "../components/Reviews";
import NotifyMe from "../components/NotifyMe";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const { has, toggle } = useWishlist();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState("");
  const [related, setRelated] = useState([]);

  const loadProduct = () => {
    api.get(`/products/${id}`).then((r) => setProduct(r.data)).catch(() => setNotFound(true));
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    setNotFound(false);
    setProduct(null);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setActive(0);
      setQty(1);
      setVariant(r.data.variants?.[0] || "");
      api
        .get("/products", { params: { category: r.data.category_slug } })
        .then((res) => setRelated(res.data.filter((p) => p.id !== id).slice(0, 4)));
    }).catch(() => setNotFound(true));
  }, [id]);

  useSeo({
    title: product ? `${product.name} — Buy Online` : undefined,
    description: product?.description,
    image: product?.images?.[0],
    path: `/product/${id}`,
  });

  if (notFound)
    return (
      <div className="py-32 text-center" data-testid="product-not-found">
        <h2 className="text-2xl font-display text-primary mb-2">Product Not Found</h2>
        <p className="text-muted-foreground mb-6">This product may have been removed or the link is invalid.</p>
        <Link to="/shop" className="bg-primary text-primary-foreground px-8 py-3 text-sm">Browse All Products</Link>
      </div>
    );

  if (!product)
    return <div className="py-32 text-center text-muted-foreground">Loading…</div>;

  const images = product.images || [];
  const discount =
    product.mrp > product.price
      ? Math.round(((product.mrp - product.price) / product.mrp) * 100)
      : 0;

  const handleAdd = () => {
    addItem(product, qty, variant);
    toast.success("Added to cart", { description: `${product.name} × ${qty}` });
  };

  const buyNow = () => {
    addItem(product, qty, variant);
    navigate("/checkout");
  };

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-8" data-testid="product-detail-page">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-6"
        data-testid="back-btn"
      >
        <ChevronLeft size={16} /> Back
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {/* Gallery */}
        <div>
          <div className="relative overflow-hidden bg-muted aspect-[4/5]">
            {images.length > 0 ? (
              <motion.img
                key={active}
                initial={{ opacity: 0.4 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4 }}
                src={resolveImg(images[active] || images[0])}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                No image available
              </div>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-3 mt-4">
              {images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActive(i)}
                  className={`w-20 aspect-square overflow-hidden border ${
                    active === i ? "border-primary" : "border-border"
                  }`}
                  data-testid={`thumb-${i}`}
                >
                  <img src={resolveImg(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="lg:sticky lg:top-28 lg:self-start">
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold">
            {product.category}
          </p>
          <h1 className="mt-3 text-3xl sm:text-4xl text-primary font-medium leading-tight">
            {product.name}
          </h1>

          {product.rating_count > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <Stars value={product.rating_avg} size={16} />
              <span className="text-sm text-muted-foreground">
                {product.rating_avg} · {product.rating_count} review{product.rating_count !== 1 ? "s" : ""}
              </span>
            </div>
          )}

          <div className="mt-5 flex items-baseline gap-3">
            <span className="text-2xl font-semibold text-primary">{inr(product.price)}</span>
            {discount > 0 && (
              <>
                <span className="text-lg text-muted-foreground line-through">{inr(product.mrp)}</span>
                <span className="text-sm text-accent font-semibold">{discount}% off</span>
              </>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-1">Inclusive of all taxes · Free shipping</p>

          <p className="mt-6 text-foreground/80 leading-relaxed font-light">{product.description}</p>

          {product.variants?.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Option</p>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v) => (
                  <button
                    key={v}
                    onClick={() => setVariant(v)}
                    className={`px-4 py-2 text-sm border transition-colors ${
                      variant === v
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border hover:border-primary"
                    }`}
                    data-testid={`variant-${v}`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Qty + actions */}
          {product.in_stock === false ? (
            <>
              <NotifyMe productId={product.id} />
              <button
                onClick={() => {
                  toggle(product.id);
                  toast.success(has(product.id) ? "Removed from wishlist" : "Saved to wishlist");
                }}
                className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                data-testid="pdp-wishlist-btn"
              >
                <Heart
                  size={16}
                  strokeWidth={1.8}
                  className={has(product.id) ? "text-accent fill-accent" : ""}
                />
                {has(product.id) ? "Saved to wishlist" : "Save to wishlist"}
              </button>
            </>
          ) : (
          <>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <div className="flex items-center border border-border">
              <button
                onClick={() => setQty(Math.max(1, qty - 1))}
                className="px-4 py-3 hover:bg-muted"
                data-testid="qty-minus"
              >
                <Minus size={16} />
              </button>
              <span className="px-5 text-sm font-semibold" data-testid="qty-value">{qty}</span>
              <button
                onClick={() => setQty(qty + 1)}
                className="px-4 py-3 hover:bg-muted"
                data-testid="qty-plus"
              >
                <Plus size={16} />
              </button>
            </div>
            <button
              onClick={handleAdd}
              className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 border border-primary text-primary px-6 py-3.5 text-sm tracking-wide hover:bg-secondary transition-colors"
              data-testid="add-to-cart-btn"
            >
              <ShoppingBag size={18} /> Add to Cart
            </button>
            <button
              onClick={() => {
                toggle(product.id);
                toast.success(has(product.id) ? "Removed from wishlist" : "Saved to wishlist");
              }}
              aria-label="Toggle wishlist"
              className="h-[46px] w-[46px] border border-border flex items-center justify-center hover:border-primary transition-colors"
              data-testid="pdp-wishlist-btn"
            >
              <Heart
                size={19}
                strokeWidth={1.8}
                className={has(product.id) ? "text-accent fill-accent" : "text-foreground/60"}
              />
            </button>
          </div>
          <button
            onClick={buyNow}
            className="mt-3 w-full bg-primary text-primary-foreground px-6 py-4 text-sm tracking-wide hover:opacity-95 transition-opacity"
            data-testid="buy-now-btn"
          >
            Buy Now
          </button>
          </>
          )}
          <a
            href={`https://wa.me/${WHATSAPP}?text=${encodeURIComponent(
              `Hi Aethel Decor, I'm interested in ${product.name}`
            )}`}
            target="_blank"
            rel="noreferrer"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary"
            data-testid="pdp-whatsapp"
          >
            <MessageCircle size={16} /> Ask about this on WhatsApp
          </a>
          <a
            href={`mailto:info@aetheldecor.com?subject=${encodeURIComponent(
              `Enquiry: ${product.name}`
            )}&body=${encodeURIComponent(
              `Hi Aethel Decor,\n\nI'm interested in: ${product.name}\nPrice: ${inr(product.price)}\nProduct link: ${window.location.href}\n\nPlease share more details.\n\nThank you`
            )}`}
            className="mt-2 w-full inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-primary"
            data-testid="pdp-email-enquiry"
            aria-label="Enquire about this product via email"
          >
            <Mail size={16} /> Email us about this product
          </a>

          {/* Spec table */}
          <div className="mt-9 border border-border">
            {[
              ["Material", product.material],
              ["Dimensions", product.dimensions],
              ["Care", product.care],
            ].map(([k, v], i) => (
              <div
                key={k}
                className={`grid grid-cols-3 text-sm ${i !== 0 ? "border-t border-border" : ""}`}
              >
                <div className="col-span-1 bg-muted px-4 py-3 font-semibold text-foreground/80">{k}</div>
                <div className="col-span-2 px-4 py-3 text-muted-foreground">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Reviews productId={product.id} onChange={loadProduct} />

      {related.length > 0 && (
        <div className="mt-24">
          <h2 className="text-2xl sm:text-3xl text-primary font-medium mb-8">You may also like</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {related.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="luxury-card group block overflow-hidden" data-testid={`related-${p.id}`}>
                <div className="overflow-hidden aspect-[4/5] rounded-t-[20px]">
                  <img
                    src={resolveImg(p.images?.[0])}
                    alt={p.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="px-4 pt-3 pb-4">
                  <h3 className="font-display text-base group-hover:text-primary transition-colors">{p.name}</h3>
                  <p className="text-sm font-semibold text-primary mt-1">{inr(p.price)}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
